import Userlabel from "@/components/AvatarComponent/Userlabel";
import ModalManager from "@/components/ModalComponent/ModalManager";
import WrappedText from "@/components/WrappedText/WrappedText";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import MQ from "@/utils/MatchQueryUtils";
import StringUtils from "@/utils/StringUtils";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Nav } from "rsuite";
import ListComponent from "../../../../configurable/components/ListComponent/ListComponent";
import i18n from "../../../../i18n";
import { AssetTypes } from "../../../../model/AssetTypes";
import CommentsModule from "../../../../modules/comments-module/CommentsModule";
import { useConstants } from "../../../../redux/hooks";
import CashBudgetBookingCard from "../../cashBudget/components/CashBudgetBookingCard";
import { AssetCashBudgetEntry } from "../../cashBudget/model/CashBudgetEntry";
import InvoiceService from "../InvoiceService";
import { Invoice, InvoiceBooking, RAConstants } from "../RAInterfaces";
import AddManualPayment from "./AddManualPayment/AddManualPayment";
import RABookInvoiceEntry from "./BookInvoices/RABookInvoiceEntry";
import { RADocumentsView } from "./Documents/RADocumentsView";
import RAInvoicePaymentKPI from "./InvoiceDetails/RAInvoicePaymentKPI";
import "./RAInvoiceDetails.scss";
import RAInvoiceHistory from "./RAInvoiceHistory";

interface Props {
  invoice: Invoice;
  initialActive?: "comments" | "history" | "documents";
}

const RAInvoiceDetails = (props: Props) => {
  const [active, setActive] = useState<"comments" | "history" | "documents">(
    "comments"
  );

  useEffect(() => {
    if (props.initialActive) {
      setActive(props.initialActive);
    }
  }, [props.initialActive]);

  if (!props.invoice) {
    return null;
  }
  return (
    <div className="ra-invoice-details">
      <Nav appearance="subtle" activeKey={active} onSelect={setActive}>
        <Nav.Item eventKey="comments">
          {i18n.t("ra:InvoiceDetails.Tabs.Comment")} (
          {props.invoice.communication?.numComments || 0})
        </Nav.Item>
        <Nav.Item eventKey="history">
          {i18n.t("ra:InvoiceDetails.Tabs.History")}
        </Nav.Item>
        <Nav.Item eventKey="documents">
          {i18n.t("ra:InvoiceDetails.Tabs.Documents", "Dokumente")}
          {/* ( */}
          {/* {(props.invoice?.data.attachments || []).length}) */}
        </Nav.Item>
      </Nav>
      {active === "comments" && <RACommentsView {...props} />}
      {active === "history" && <RAHistoryView {...props} />}
      {active === "documents" && <RAPaymentsDocumentViewContainer {...props} />}
    </div>
  );
};
export default RAInvoiceDetails;

export const RACommentsView = (props: Props) => {
  return (
    <div className="comments-container">
      <CommentsModule
        assetId={props.invoice._id}
        assetType={AssetTypes.Invoicing.Invoice}
        identifier={`ra-invoice-details-comments`}
        disableEmailCommunication
        type={props.invoice.data.type}
      />
    </div>
  );
};

export const RAHistoryView = (props: Props) => {
  return (
    <div className="history-container">
      <RAInvoiceHistory
        entries={props.invoice?.data.history || []}
        asset={props.invoice}
      />
    </div>
  );
};
export const RAAccountingView = (props: Props) => {
  const constants = useConstants<RAConstants>();
  return (
    <div className={`ra-accounting-container`}>
      <ListComponent
        asPost
        assetType={AssetTypes.Invoicing.Booking}
        additionalMatchQuery={{
          type: "op",
          op: "eq",
          name: "data.linkedAsset.assetId",
          value: props.invoice?._id,
        }}
        identifier="ra-invoice-details-accounting"
        render={(booking: InvoiceBooking) => (
          <div className={`ra-accounting-entry`}>
            <RABookInvoiceEntry booking={booking} />
          </div>
        )}
      />
    </div>
  );
};

export const RAPaymentsView = (props: Props) => {
  const constants = useConstants<RAConstants>();
  return (
    <div className={`ra-payments-container`}>
      <div className={`padding-10`}>
        <RAInvoicePaymentKPI invoice={props.invoice} />
      </div>
      <div className={`actions __flex __justify-end`}>
        <BFButton
          appearance="outline"
          size="xs"
          onClick={() => {
            ModalManager.show({
              noPadding: true,
              size: "sm",
              content: (state, setState, close) => (
                <AddManualPayment
                  invoice={props.invoice}
                  onClose={close}
                  onSuccess={() => {
                    close();
                  }}
                />
              ),
            });
          }}
        >
          {i18n.t(
            "ra:InvoiceDetails.Payments.AddManualPayment",
            "Manuelle Zahlung hinzufügen"
          )}
        </BFButton>
      </div>
      <div className={`payments-container`}>
        <ListComponent
          asPost
          assetType={AssetTypes.CashBudget.Booking}
          additionalMatchQuery={MQ.in(
            "_id",
            props.invoice.data.opos?.bookedInfo.map((e) => e.bookingId)
          )}
          renderPrepend={() => {
            // props.invoice.data.
            const manuallyBooked = props.invoice.data.opos?.manuallyInfo || [];
            if (manuallyBooked.length) {
              return (
                <div className={`manually-booked-container padding-10`}>
                  <div className={`manually-booked-entries __card`}>
                    <div className={`manually-booked-header __h3`}>
                      {i18n.t(
                        "ra:InvoiceDetails.Payments.ManuallyBooked",
                        "Manuell gebucht"
                      )}
                    </div>
                    {manuallyBooked.map((e) => (
                      <div className={`manually-booked-entry`} key={e.id}>
                        <div className={`first-row`}>
                          <div className={`manually-booked-entry-comment`}>
                            <WrappedText text={e.comment} />
                          </div>
                          <div
                            className={classNames(
                              `manually-booked-entry-value`,
                              {
                                "__color-negative": e.value < 0,
                                "__color-positive": e.value > 0,
                              }
                            )}
                          >
                            {StringUtils.formatCurrency(e.value)}
                          </div>
                        </div>
                        <div className={`second-row`}>
                          <div
                            className={`manually-booked-entry-date __sub-text`}
                          >
                            {StringUtils.formatDate(e.date)}
                          </div>
                          <div
                            className={`manually-booked-entry-user __sub-text`}
                          >
                            <Userlabel id={e.user} />
                          </div>
                          <div className={`fill`} />

                          <BFButton
                            appearance="link"
                            size="xs"
                            noPadding
                            onClick={() => {
                              ModalManager.confirm({
                                message: i18n.t(
                                  "ra:InvoiceDetails.Payments.DeleteConfirmation",
                                  "Soll diese manuelle Zahlung wirklich gelöscht werden?"
                                ),
                                confirmButtonText: i18n.t(
                                  "Global.Buttons.delete"
                                ),
                                onConfirm: async () => {
                                  await InvoiceService.deleteManualBooking(
                                    props.invoice._id,
                                    e.id
                                  );
                                },
                              });
                            }}
                          >
                            {i18n.t(
                              "ra:InvoiceDetails.Payments.Delete",
                              "Löschen"
                            )}
                          </BFButton>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          }}
          identifier="ra-invoice-details-payments"
          emptyText={i18n.t(
            "ra:InvoiceDetails.NoPayments",
            "Keine Zahlungen gebucht."
          )}
          render={(booking: AssetCashBudgetEntry) => (
            <CashBudgetBookingCard
              booking={booking}
              showCategory
              expanded={true}
            />
          )}
        />
      </div>
    </div>
  );
};

const RAPaymentsDocumentViewContainer = ({ invoice }: Props) => {
  return (
    <div className={`ra-payments-document-container`}>
      <RADocumentsView invoice={invoice} />
    </div>
  );
};
